import React, { useState, useEffect, useCallback, useRef } from 'react';
import BasicDrawer from '../../../components/Drawer';
import { Controller, useForm } from 'react-hook-form';
import {
    Button,
    TextField,
    PaginatedSelect,
    buildPropertyOption,
} from '../../../components';
import LabelWithSelect from '../../../components/LabelWithSelect';
import { ApiCaller, Colors, Utils } from '../../../config';
import {
    AutoComplete,
    Checkbox,
    Col,
    ConfigProvider,
    DatePicker,
    Row,
    Select,
    Switch,
} from 'antd';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import RangeDatePicker from '../../../components/Picker/RangePicker';
import { TimePicker } from 'antd';
import { useMutation, useQuery } from 'react-query';
import {
    getActiveUsers,
    getAllUsers,
    getEligibiity,
    getPropertiesPage,
    getPropertyDetail,
} from '../../../config/getApis';
import LabelWithField from '../../../components/LabelWithField';
import {
    AssignNormalShiftSchema,
    AssignOpenShiftSchema,
} from '../../../config/Schema';
import { useDispatch, useSelector } from 'react-redux';
import { ShiftSchedulerAction } from '../../../store/actions';
import avatar from '../../../assets/images/avatar.jpg';
import useColors from '../../../hooks/useColors';
import { recurringFrequency, shiftBreaks } from '../../../config/variables';

let usersStatus = 'active';
const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

/**
 * Parses shorthand digit-only time entry into a dayjs object.
 * Handles AM/PM suffixes, colons, and raw digit strings.
 * Examples:
 *   "2"      → 02:00   "200"   → 02:00
 *   "930"    → 09:30   "1430"  → 14:30
 *   "2 PM"   → 14:00   "15"    → 15:00 (24h), 03:00 PM (12h display)
 * Returns null if the input cannot produce a valid time.
 */
const parseShorthandTime = (raw) => {
    if (!raw) return null;
    const str = String(raw).trim();
    const isPM = /pm/i.test(str);
    const isAM = /am/i.test(str);
    const digits = str.replace(/[^\d]/g, '');
    if (!digits) return null;
    let h, m;
    if (digits.length <= 2) {
        h = parseInt(digits, 10);
        m = 0;
    } else if (digits.length === 3) {
        h = parseInt(digits[0], 10);
        m = parseInt(digits.slice(1), 10);
    } else {
        h = parseInt(digits.slice(0, 2), 10);
        m = parseInt(digits.slice(2, 4), 10);
    }
    // Apply explicit AM/PM modifier
    if (isPM && h < 12) h += 12;
    if (isAM && h === 12) h = 0;
    if (h > 23 || m > 59) return null;
    return dayjs().hour(h).minute(m).second(0);
};

/**
 * SmartTimePicker — a text input with live minute suggestions.
 *
 * Behaviour (matches the screenshot):
 *  • User types "15"  → dropdown: 15:00 / 15:15 / 15:30 / 15:45
 *  • In 12h mode, hours > 12 are automatically shown as PM
 *  • Pressing Enter / Tab or blurring commits the typed value
 *  • Selecting a suggestion commits immediately
 */
const SmartTimePicker = ({ value, onChange, use12Hours, className }) => {
    const [inputText, setInputText] = useState('');
    const [options, setOptions] = useState([]);

    // Format a 24-h dayjs for display according to the active mode
    const fmt = (d) =>
        d && dayjs.isDayjs(d) && d.isValid()
            ? use12Hours ? d.format('hh:mm A') : d.format('HH:mm')
            : '';

    // Keep display text in sync when external value changes (pre-fill / reset)
    useEffect(() => {
        setInputText(fmt(value));
    }, [value, use12Hours]);

    // Build :00 / :15 / :30 / :45 suggestions for a typed hour
    const buildOptions = (text) => {
        const digits = text.replace(/[^\d]/g, '');
        if (!digits || digits.length > 2) return [];
        const hour = parseInt(digits, 10);
        if (hour > 23) return [];
        return [0, 15, 30, 45].map((min) => {
            // Internal value is always stored as HH:mm
            const hh = String(hour).padStart(2, '0');
            const mm = String(min).padStart(2, '0');
            const val = `${hh}:${mm}`;
            let label;
            if (use12Hours) {
                const period = hour >= 12 ? 'PM' : 'AM';
                const displayH = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                label = `${displayH}:${mm} ${period}`;
            } else {
                label = val;
            }
            return { value: val, label };
        });
    };

    // Commit whatever is in the text box
    const commit = (raw) => {
        const parsed = parseShorthandTime(raw);
        if (parsed && parsed.isValid()) {
            onChange(parsed);
            setInputText(fmt(parsed));
        } else if (value && dayjs.isDayjs(value) && value.isValid()) {
            setInputText(fmt(value)); // revert to last valid
        } else {
            setInputText('');
        }
        setOptions([]);
    };

    return (
        <AutoComplete
            value={inputText}
            options={options}
            onChange={(text) => {
                setInputText(text);
                setOptions(buildOptions(text));
            }}
            onSelect={(val) => {
                const parsed = dayjs(val, 'HH:mm');
                onChange(parsed);
                setInputText(fmt(parsed));
                setOptions([]);
            }}
            onBlur={() => commit(inputText)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Tab') commit(inputText);
            }}
            className={className}
            placeholder={use12Hours ? 'hh:mm AM/PM' : 'HH:mm'}
        />
    );
};

const AssignShiftDrawer = ({
    drawerStates,
    setDrawerStates,
    shiftState,
    setShiftState,
    openShiftDetailData,
    setAssignShiftCase,
    setOpenShiftDetail = () => { },
}) => {
    const { colorPrimary } = useColors();
    const dateFormat = useSelector((state) => state.AppReducer?.dateFormat);
    const timeFormat = useSelector((state) => state.AppReducer?.timeFormat);

    const { data: userOptions } = useQuery(
        ['users', { usersStatus }],
        () => getAllUsers('', usersStatus, 'fullName'),
        {
            enabled: drawerStates.typeOfShift === 'normal',
        },
    );

    // Properties are loaded one page at a time by the dropdown itself (search +
    // infinite scroll), so we keep the raw objects we have seen in a cache to be
    // able to derive addresses/locations for whichever one gets selected. Options
    // themselves come from the shared builder so every screen renders them alike.
    const propertyCacheRef = useRef({});

    const toPropertyOption = (p) => {
        propertyCacheRef.current[p.id] = p;
        return buildPropertyOption(p);
    };

    // One page of properties for the dropdown, honouring the typed search term
    const fetchPropertiesPage = useCallback(async ({ page, search }) => {
        const { data, hasMore } = await getPropertiesPage({ page, search });
        return { options: data.map(toPropertyOption), hasMore };
    }, []);

    // Track the currently selected property object
    const [selectedProperty, setSelectedProperty] = useState(null);

    // Keeps the selected property visible in the dropdown even when it is not on
    // a loaded page (e.g. while editing a shift, or after a search)
    const selectedPropertyOption = selectedProperty
        ? [toPropertyOption(selectedProperty)]
        : [];

    // Resolves a property id to its full object (cache first, API otherwise) so
    // that the Address/Location dropdowns can be populated
    const resolveProperty = async (id) => {
        if (!id) return null;
        const cached = propertyCacheRef.current[id];
        if (cached?.addresses || cached?.locations) return cached;
        const detail = await getPropertyDetail(id);
        if (detail) propertyCacheRef.current[id] = detail;
        return detail || cached || null;
    };

    // Address options derived from selected property
    const addressOptions = (selectedProperty?.addresses || []).map((addr) => ({
        label: [
            addr.streetNumber,
            addr.streetName,
            addr.city,
            addr.state,
            addr.zip,
        ]
            .filter(Boolean)
            .join(', '),
        value: addr.id,
    }));

    // Location options derived from selected property
    const locationOptions = (selectedProperty?.locations || []).map((loc) => ({
        label: loc.name,
        value: loc.id,
    }));
    const { data: elegibilityOptions } = useQuery(
        'eligibilites',
        getEligibiity,
        {
            enabled:
                drawerStates.typeOfShift == 'normal' ||
                drawerStates.typeOfShift == 'open',
        },
    );

    const schema =
        drawerStates.typeOfShift == 'open'
            ? AssignOpenShiftSchema
            : AssignNormalShiftSchema;

    const defaultOpenShiftValues = {
        break: '',
        numberOfAssignees: '',
        propertyId: '',
        eligibilityId: '',
        recurringFrequency: '',
        notes: '',
        addressId: '',
        locationId: '',
        assignTo: '',
        isPublished: true,
        recurringDays: [],
    };
    const defaultNormalShiftValues = {
        break: '',
        propertyId: '',
        eligibilityId: '',
        recurringFrequency: '',
        notes: '',
        addressId: '',
        locationId: '',
        assignTo: '',
        isPublished: true,
        recurringDays: [],
    };
    const { handleSubmit, control, reset, setValue, setError, clearErrors } =
        useForm({
            resolver: yupResolver(schema),
            mode: 'onChange',
            defaultValues:
                drawerStates.typeOfShift == 'open'
                    ? defaultOpenShiftValues
                    : defaultNormalShiftValues,
        });

    const dispatch = useDispatch();
    console.log(drawerStates, 'drawerStates.typeOfShift');

    // for time and date only, we are making them controlled component like this, rest will be handle by useform.
    const [state, setState] = useState({
        startTime: '',
        endTime: '',
        startDate: '',
        endDate: '',
        shiftDate: '',
    });
    const [shiftType, setShiftType] = useState('reoccurring');
    // validations only for time and date, rest will be handle by the useForm and yup.
    const [validation, setValidations] = useState({
        startTime: '',
        endTime: '',
        startDate: '',
        endDate: '',
        shiftDate: '',
    });

    // normal shift creation
    const normalShiftMutation = useMutation(
        async (data) => {
            let users = [];
            let assign = data?.assignTo;
            users.push(assign);
            data.assignTo = users;
            data.isOpen = false;

            let {
                shiftType,
                startDate,
                endDate,
                shiftDate,
                numberOfAssignees,
                recurringFrequency,
                ...restData
            } = data;

            if (shiftType === 'reoccurring') {
                // Re Occurring (swapped value): send startDate + endDate, set isRecurring true
                restData.startDate = startDate
                    ? dayjs(startDate, dateFormat).format('YYYY-MM-DD')
                    : null;
                restData.endDate = endDate
                    ? dayjs(endDate, dateFormat).format('YYYY-MM-DD')
                    : null;
                restData.isRecurring = true;
                restData.recurringFrequency = recurringFrequency;
                restData.recurringDays = data?.recurringDays;
            } else {
                // Non Re Occurring: send shiftDate formatted as YYYY-MM-DD
                restData.shiftDate = shiftDate
                    ? dayjs(shiftDate, dateFormat).format('YYYY-MM-DD')
                    : null;
                restData.isRecurring = false;
                delete restData?.recurringFrequency;
            }

            console.log('Sending Normal Shift Payload:', restData);
            let resp = await ApiCaller.Post('/shift', restData);
            return resp;
        },
        {
            onSuccess: (resp) => {
                const { schedule, user, eligiblity } = shiftState?.filterDrawer;
                const { tabKey } = shiftState;
                Utils.showMessage('success', resp?.data?.message);
                setDrawerStates((prev) => ({
                    ...prev,
                    shiftTypeDrawer: false,
                    typeOfShift: '',
                }));
                setAssignShiftCase(true);
                dispatch(ShiftSchedulerAction.ClearShifts());
                setShiftState((prev) => ({
                    ...prev,
                    page: shiftState?.page,
                    tabKey: tabKey,
                    filterDrawer: {
                        schedule: schedule,
                        user: user,
                        eligiblity: eligiblity,
                    },
                }));
                reset(
                    drawerStates.typeOfShift == 'open'
                        ? defaultOpenShiftValues
                        : defaultNormalShiftValues,
                );
                setValue('recurringDays', []);
                setValue('startTime', '');
                setValue('endTime', '');
                setValue('startDate', '');
                setValue('endDate', '');
                setValue('shiftDate', '');
                setState({
                    startTime: '',
                    endTime: '',
                    startDate: '',
                    endDate: '',
                    shiftDate: '',
                });
                // dispatch(
                //     ShiftSchedulerAction.GetShifts({
                //         limit: 5,
                //         page: 1,
                //         tabKey: tabKey,
                //     }),
                // );
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                return;
            },
            onError: (err) => {
                Utils.showMessage('error', err?.response?.data?.message);
            },
        },
    );

    // Mutation to update shift details
    const shiftUpdateMutation = useMutation(
        async (data) => {
            // specific formatting if needed
            console.log(data, '=========------');
            const payload = { ...data };
            // Time formatting
            // Location Parsing
            if (
                payload.locationId &&
                typeof payload.locationId === 'string' &&
                payload.locationId.includes(',')
            ) {
                let addressStr = payload.locationId.split(',');
                let locationPart = addressStr[0];
                let addressPart = addressStr[1];

                let locId = locationPart.split(':')[1];
                let addrId = addressPart.split(':')[1];

                payload.locationId = locId;
                payload.addressId = addrId;
            }

            if (payload?.isRecurring) {
                payload.startDate = !payload.startDate
                    ? moment(payload.shiftDate).format('YYYY-MM-DD')
                    : moment(payload.startDate).format('YYYY-MM-DD');
                payload.endDate = !payload.endDate
                    ? moment(payload.shiftDate).format('YYYY-MM-DD')
                    : moment(payload.endDate).format('YYYY-MM-DD');
            } else {
                payload.isRecurring = false;
                payload.startDate = moment(payload.shiftDate).format(
                    'YYYY-MM-DD',
                );
                payload.endDate = moment(payload.shiftDate).format(
                    'YYYY-MM-DD',
                );
            }

            if (
                !payload.recurringFrequency ||
                payload.recurringFrequency === 'undefined'
            ) {
                delete payload.recurringFrequency;
            }

            if (payload.usersId) {
                payload.assignTo = payload.usersId;
                delete payload.usersId;
            }

            let resp = await ApiCaller.Put(
                `/shift/${openShiftDetailData?.id}`,
                payload,
            );
            return resp;
        },
        {
            onSuccess: (resp) => {
                const { schedule, user, eligiblity } = shiftState?.filterDrawer;
                const { tabKey } = shiftState;
                Utils.showMessage('success', resp?.data?.message);
                setDrawerStates((prev) => ({
                    ...prev,
                    shiftTypeDrawer: false,
                    typeOfShift: '',
                }));
                setAssignShiftCase(true);
                dispatch(ShiftSchedulerAction.ClearShifts());
                setShiftState((prev) => ({
                    ...prev,
                    page: shiftState?.page,
                    tabKey: tabKey,
                    filterDrawer: {
                        schedule: schedule,
                        user: user,
                        eligiblity: eligiblity,
                    },
                }));
                reset(
                    drawerStates.typeOfShift === 'open'
                        ? defaultOpenShiftValues
                        : defaultNormalShiftValues,
                );
                setValue('recurringDays', []);
                setValue('startTime', '');
                setValue('endTime', '');
                setValue('startDate', '');
                setValue('endDate', '');
                setValue('shiftDate', '');
                setState({
                    startTime: '',
                    endTime: '',
                    startDate: '',
                    endDate: '',
                    shiftDate: '',
                });
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                shiftCloseHandler();
                setAssignShiftCase(true);
                dispatch(ShiftSchedulerAction.ClearShifts());
            },
            onError: (err) => {
                Utils.showMessage('error', err?.response?.data?.message);
            },
        },
    );

    // open shift creation
    // const openShiftMutation = useMutation(
    //     async (data) => {
    //         let { assignTo, ...restData } = data;
    //         restData.isRecurring = true;
    //         restData.isOpen = true;

    //         if (restData?.shiftDate == '') {
    //             let { shiftDate, ...restOpenData } = restData;
    //             let resp = await ApiCaller.Post('/shift', restOpenData);
    //             return resp;
    //         } else {
    //             let resp = await ApiCaller.Post('/shift', restData);
    //             return resp;
    //         }
    //     },
    //     {
    //         onSuccess: (resp) => {
    //             const { schedule, user, eligiblity } = shiftState?.filterDrawer;
    //             const { tabKey } = shiftState;
    //             Utils.showMessage('success', resp?.data?.message);
    //             setDrawerStates((prev) => ({
    //                 ...prev,
    //                 shiftTypeDrawer: false,
    //                 typeOfShift: '',
    //             }));
    //             setShiftState((prev) => ({
    //                 ...prev,
    //                 page: shiftState?.page,
    //                 tabKey: tabKey,
    //                 filterDrawer: {
    //                     schedule: schedule,
    //                     user: user,
    //                     eligiblity: eligiblity,
    //                 },
    //             }));
    //             reset(
    //                 drawerStates.typeOfShift == 'open'
    //                     ? defaultOpenShiftValues
    //                     : defaultNormalShiftValues,
    //             );
    //             setValue('startTime', '');
    //             setValue('endTime', '');
    //             setValue('startDate', '');
    //             setValue('endDate', '');
    //             setValue('shiftDate', '');
    //             setState({
    //                 startTime: '',
    //                 endTime: '',
    //                 startDate: '',
    //                 endDate: '',
    //                 shiftDate: '',
    //             });
    //             setValidations({
    //                 startTime: '',
    //                 endTime: '',
    //                 shiftDate: '',
    //                 startDate: '',
    //                 endDate: '',
    //             });
    //             dispatch(
    //                 ShiftSchedulerAction.GetShifts({
    //                     limit: 5,
    //                     page: 1,
    //                     tabKey: tabKey,
    //                 }),
    //             );
    //             return;
    //         },
    //         onError: (err) => {
    //             Utils.showMessage('error', err?.response?.data?.message);
    //         },
    //     },
    // );

    console.log('shiftsssssssssssss', shiftType);
    const openShiftMutation = useMutation(
        async (data) => {
            let {
                assignTo,
                shiftType,
                startDate,
                endDate,
                shiftDate,
                ...restData
            } = data;

            // Formatting based on shiftType (swapped values)
            if (shiftType === 'reoccurring') {
                // Re Occurring: send startDate + endDate
                console.log('Inside reoccuring (open)');
                restData.startDate = startDate
                    ? dayjs(startDate, dateFormat).format('YYYY-MM-DD')
                    : null;
                restData.endDate = endDate
                    ? dayjs(endDate, dateFormat).format('YYYY-MM-DD')
                    : null;
                restData.isRecurring = true;
                restData.recurringDays = data?.recurringDays;
            } else {
                // Non Re Occurring: send shiftDate formatted as YYYY-MM-DD
                console.log('Inside non-reoccuring (open)');
                restData.isRecurring = false;
                delete restData?.recurringFrequency;
                restData.shiftDate = shiftDate
                    ? dayjs(shiftDate, dateFormat).format('YYYY-MM-DD')
                    : null;
            }

            restData.isOpen = true;

            console.log('Sending Open Shift Payload:', restData);

            let resp;
            if (!restData?.shiftDate && !restData?.startDate) {
                // Fallback for safety
                let { shiftDate, ...restOpenData } = restData;
                resp = await ApiCaller.Post('/shift', restOpenData);
            } else {
                resp = await ApiCaller.Post('/shift', restData);
            }
            return resp;
        },
        {
            onSuccess: (resp) => {
                const { schedule, user, eligiblity } = shiftState?.filterDrawer;
                const { tabKey } = shiftState;
                Utils.showMessage('success', resp?.data?.message);
                setDrawerStates((prev) => ({
                    ...prev,
                    shiftTypeDrawer: false,
                    typeOfShift: '',
                }));
                setAssignShiftCase(true);
                dispatch(ShiftSchedulerAction.ClearShifts());
                setShiftState((prev) => ({
                    ...prev,
                    page: shiftState?.page,
                    tabKey: tabKey,
                    filterDrawer: {
                        schedule: schedule,
                        user: user,
                        eligiblity: eligiblity,
                    },
                }));
                reset(
                    drawerStates.typeOfShift === 'open'
                        ? defaultOpenShiftValues
                        : defaultNormalShiftValues,
                );
                setValue('recurringDays', []);
                setValue('startTime', '');
                setValue('endTime', '');
                setValue('startDate', '');
                setValue('endDate', '');
                setValue('shiftDate', '');
                setState({
                    startTime: '',
                    endTime: '',
                    startDate: '',
                    endDate: '',
                    shiftDate: '',
                });
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                // dispatch(
                //     ShiftSchedulerAction.GetShifts({
                //         limit: 5,
                //         page: 1,
                //         tabKey: tabKey,
                //     }),
                // );
                return;
            },
            onError: (err) => {
                Utils.showMessage('error', err?.response?.data?.message);
            },
        },
    );

    const shiftSaveHandler = (data) => {
        console.log('shifttypeeeesssssssssssssss', shiftType);

        data.shiftType = shiftType;
        console.log('devvv', data);

        // Helper to parse locationId into addressId + locationId
        const parseLocation = (locationId) => {
            if (
                !locationId ||
                typeof locationId !== 'string' ||
                !locationId.includes(',')
            )
                return;
            // if (!locationId || typeof locationId !== 'string') return;

            let parts = locationId.split(',');
            parts.forEach((part) => {
                let [key, value] = part.split(':').map((s) => s.trim());
                if (key === 'location_id') data.locationId = value;
                if (key === 'address_id') data.addressId = value;
            });
        };

        // Check if the shift type is normal
        clearErrors('recurringFrequency');
        clearErrors('recurringDays');

        if (drawerStates?.typeOfShift === 'normal') {
            if (shiftType === 'reoccurring' && !data?.recurringFrequency) {
                setError('recurringFrequency', {
                    type: 'manual',
                    message: 'Repeat Shift is required',
                });
                return;
            }
            if (
                shiftType === 'reoccurring' &&
                (!data?.recurringDays || data?.recurringDays?.length === 0)
            ) {
                setError('recurringDays', {
                    type: 'manual',
                    message: 'Repeat Days are required',
                });
                return;
            }
            // Validate for normal shift (startTime, endTime are always required)
            if (!state?.startTime || !state?.endTime) {
                setValidations({
                    startTime: !state?.startTime
                        ? 'Start time is required'
                        : '',
                    endTime: !state?.endTime ? 'End time is required' : '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                return;
            }

            // Validate date based on shiftType
            if (shiftType === 'non-reoccurring' && !state?.shiftDate) {
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: 'Shift Date is required',
                    startDate: '',
                    endDate: '',
                });
                return;
            }

            if (shiftType === 'reoccurring' && !state?.startDate) {
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: 'Date range is required',
                    endDate: '',
                });
                return;
            }

            setValidations({
                startTime: '',
                endTime: '',
                shiftDate: '',
                startDate: '',
                endDate: '',
            });
            parseLocation(data?.locationId);
            if (openShiftDetailData && openShiftDetailData?.ResetTime) {
                console.log(
                    'When its Work openShiftDetailData',
                    openShiftDetailData,
                );
                shiftUpdateMutation.mutate({
                    ...data,
                    startTime: openShiftDetailData?.isDayView
                        ? dayjs(
                            dayjs(openShiftDetailData?.ResetTime, 'hh'),
                            'HH:mm',
                        ).format('HH:mm')
                        : dayjs(data?.startTime, timeFormat).format('HH:mm') ===
                            'Invalid Date'
                            ? moment(data?.startTime, timeFormat).format('HH:mm')
                            : dayjs(data?.startTime, timeFormat).format('HH:mm'),
                    repeatDays: data?.recurringDays,
                    shiftDate: openShiftDetailData?.isDayView
                        ? openShiftDetailData?.shiftDate
                        : openShiftDetailData?.ResetTime,
                    // ? moment(data.shiftDate).utc().format()
                    // : data.shiftDate,
                    isOpen: !openShiftDetailData?.isNormalToOpen,
                });
            } else {
                console.log(
                    'When its Work normalShiftMutation',
                    openShiftDetailData,
                );
                normalShiftMutation.mutate({
                    ...data,
                    // For new shifts openShiftDetailData is null, so fall back to the user-picked state.shiftDate
                    shiftDate: openShiftDetailData?.shiftDate || state.shiftDate,
                    isOpen: false,
                    repeatDays: data?.recurringDays,
                });
            }
        } else {
            if (shiftType === 'reoccurring' && !data?.recurringFrequency) {
                setError('recurringFrequency', {
                    type: 'manual',
                    message: 'Repeat Shift is required',
                });
                return;
            }
            if (
                shiftType === 'reoccurring' &&
                (!data?.recurringDays || data?.recurringDays?.length === 0)
            ) {
                setError('recurringDays', {
                    type: 'manual',
                    message: 'Repeat Days are required',
                });
                return;
            }

            // Validate for open shift (startTime, endTime are always required)
            if (!state?.startTime || !state?.endTime) {
                setValidations({
                    startTime: !state?.startTime
                        ? 'Start time is required'
                        : '',
                    endTime: !state?.endTime ? 'End time is required' : '',
                    shiftDate: '',
                    startDate: '',
                    endDate: '',
                });
                return;
            }

            // Validate date based on shiftType
            if (shiftType === 'non-reoccurring' && !state?.shiftDate) {
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: 'Shift Date is required',
                    startDate: '',
                    endDate: '',
                });
                return;
            }

            if (shiftType === 'reoccurring' && !state?.startDate) {
                setValidations({
                    startTime: '',
                    endTime: '',
                    shiftDate: '',
                    startDate: 'Date range is required',
                    endDate: '',
                });
                return;
            }

            setValidations({
                startTime: '',
                endTime: '',
                shiftDate: '',
                startDate: '',
                endDate: '',
            });

            parseLocation(data?.locationId);
            if (openShiftDetailData && openShiftDetailData?.ResetTime) {
                console.log(
                    'When its Work normalShiftMutation 2',
                    openShiftDetailData,
                );
                let newDate = { ...data };
                delete newDate.assignTo;
                delete newDate.usersId;
                shiftUpdateMutation.mutate({
                    ...newDate,
                    startTime: openShiftDetailData?.isDayView
                        ? dayjs(
                            dayjs(openShiftDetailData?.ResetTime, 'hh'),
                            'HH:mm',
                        ).format('HH:mm')
                        : dayjs(data?.startTime).format('HH:mm'),
                    repeatDays: data?.recurringDays,
                    shiftDate: openShiftDetailData?.isDayView
                        ? data.shiftDate//moment(data.shiftDate).utc().format()
                        : openShiftDetailData?.ResetTime,
                    // openShiftDetailData?.isDayView
                    // moment(data.shiftDate).utc().format(),
                    // : data.shiftDate,
                    isOpen: !openShiftDetailData?.isNormalToOpen,
                });
            } else {
                console.log(
                    'When its Work openShiftMutation 2',
                    openShiftDetailData,
                );
                openShiftMutation.mutate({
                    ...data,
                    // For new shifts openShiftDetailData is null, so fall back to the user-picked state.shiftDate
                    shiftDate: openShiftDetailData?.shiftDate || state.shiftDate,
                    repeatDays: data?.recurringDays,
                    isOpen: true,
                });
            }
        }
    };

    const shiftCloseHandler = () => {
        setOpenShiftDetail(null);
        setShiftType('non-reoccurring');
        setSelectedProperty(null);
        setDrawerStates((prev) => ({ ...prev, shiftTypeDrawer: false }));
        reset(
            drawerStates.typeOfShift == 'open'
                ? defaultOpenShiftValues
                : defaultNormalShiftValues,
        );
        setState({
            startTime: '',
            endTime: '',
            startDate: '',
            endDate: '',
            shiftDate: '',
        });
        setValidations({
            startTime: '',
            endTime: '',
            shiftDate: '',
            startDate: '',
            endDate: '',
        });
    };
    useEffect(() => {
        if (!openShiftDetailData && drawerStates?.shiftTypeDrawer && drawerStates?.prefilledData) {
            const { date, time, resourceId } = drawerStates.prefilledData;

            const formattedDate = date ? dayjs(date) : null;
            const formattedTime = time !== undefined ? `${time.toString().padStart(2, '0')}:00` : '';

            const defaultVals = drawerStates.typeOfShift === 'open' ? defaultOpenShiftValues : defaultNormalShiftValues;

            const newFormValues = {
                ...defaultVals,
                assignTo: resourceId || '',
                usersId: resourceId ? [resourceId] : [],
                shiftDate: formattedDate,
                startDate: formattedDate,
                endDate: formattedDate,
                startTime: time !== undefined ? dayjs(formattedTime, 'HH:mm') : null,
            };

            reset(newFormValues);

            setState((pre) => ({
                ...pre,
                shiftDate: formattedDate,
                startDate: formattedDate,
                endDate: formattedDate,
                startTime: time !== undefined ? dayjs(formattedTime, 'HH:mm') : null,
            }));

            setShiftType('non-reoccurring');
        }
    }, [drawerStates?.shiftTypeDrawer, drawerStates?.prefilledData, openShiftDetailData, reset]);

    useEffect(() => {
        if (openShiftDetailData) {
            // Extract user IDs from shiftUser array
            let assignedUserIds = [openShiftDetailData?.ResourceAssignTo];

            const NewShiftDate = openShiftDetailData?.isDayView
                ? openShiftDetailData.shiftDate
                    ? dayjs(openShiftDetailData.shiftDate)
                    : null
                : dayjs(openShiftDetailData.ResetTime, 'YYYY-MM-DD').format(
                    dateFormat,
                );
            const NewStartDate = openShiftDetailData?.isDayView
                ? openShiftDetailData.startDate
                    ? dayjs(openShiftDetailData.startDate)
                    : null
                : dayjs(openShiftDetailData.ResetTime);

            setShiftType(
                openShiftDetailData?.recurring
                    ? 'reoccurring'
                    : 'non-reoccurring',
            );
            const NewEndDate = openShiftDetailData?.isDayView
                ? openShiftDetailData.endDate
                    ? dayjs(openShiftDetailData.endDate)
                    : null
                : dayjs(openShiftDetailData.ResetTime);
            reset({
                usersId: assignedUserIds,
                numberOfAssignees: openShiftDetailData?.numberOfAssignees,
                assignTo: openShiftDetailData?.ResourceAssignTo,
                startTime: openShiftDetailData?.isDayView
                    ? dayjs(
                        dayjs(openShiftDetailData?.ResetTime, 'hh'),
                        'HH:mm',
                    ).format('HH:mm')
                    : openShiftDetailData.startTime
                        ? dayjs(openShiftDetailData.startTime, 'HH:mm')
                        : null,
                endTime: openShiftDetailData.endTime
                    ? dayjs(openShiftDetailData.endTime, 'HH:mm').format(
                        'HH:mm',
                    )
                    : null,
                shiftDate: NewShiftDate,
                startDate: NewStartDate,
                endDate: NewEndDate,
                propertyId: openShiftDetailData.property?.id,
                eligibilityId: openShiftDetailData.eligibilitity?.id,
                recurringFrequency: openShiftDetailData.recurringFrequency,
                break: openShiftDetailData.break,
                notes: openShiftDetailData.notes,
                published: openShiftDetailData.published ?? true, // Default to true if undefined
                locationId:
                    (openShiftDetailData?.locationId ||
                        openShiftDetailData?.location?.id) &&
                        (openShiftDetailData?.addressId ||
                            openShiftDetailData?.address?.id)
                        ? `location_id:${openShiftDetailData?.locationId || openShiftDetailData?.location?.id}, address_id:${openShiftDetailData?.addressId || openShiftDetailData?.address?.id}`
                        : '',
            });

            setState((pre) => ({
                ...pre,
                startTime: openShiftDetailData?.isDayView
                    ? dayjs(
                        dayjs(openShiftDetailData?.ResetTime, 'hh'),
                        'HH:mm',
                    )
                    : openShiftDetailData.startTime
                        ? dayjs(openShiftDetailData.startTime, 'HH:mm')
                        : null,
                endTime: openShiftDetailData.endTime
                    ? dayjs(openShiftDetailData.endTime, 'HH:mm')
                    : null,
                shiftDate: NewShiftDate,
                startDate: NewStartDate,
                endDate: NewEndDate,
            }));

            if (openShiftDetailData.property?.id) {
                // Pre-select property so address/location options populate.
                // Seed the cache with what the shift already carries, so the
                // dropdown can label it before the detail call resolves.
                propertyCacheRef.current[openShiftDetailData.property.id] = {
                    ...(propertyCacheRef.current[
                        openShiftDetailData.property.id
                    ] || {}),
                    ...openShiftDetailData.property,
                };
                setSelectedProperty(
                    propertyCacheRef.current[openShiftDetailData.property.id],
                );
                resolveProperty(openShiftDetailData.property.id).then(
                    (property) => property && setSelectedProperty(property),
                );
            }
        }
    }, [
        openShiftDetailData,
        drawerStates.openShiftDetailDrawer,
        reset,
        dispatch,
    ]);
    const propertyApiHandler = async (value) => {
        // Clear dependent fields
        setValue('addressId', '');
        setValue('locationId', '');
        if (!value) {
            setSelectedProperty(null);
            return;
        }
        // Resolve selected property for address/location derivation
        const property = await resolveProperty(value);
        setSelectedProperty(property);
    };
    const { user } = useSelector((state) => state?.AppReducer);
    const userRole = user?.userRole[0];

    const DaysOfWeek = () => {
        return (
            <Col span={24} className="mb-4">
                <Controller
                    name="recurringDays"
                    control={control}
                    render={({ field: { value, onChange }, fieldState }) => (
                        <div className="flex row flex-col">
                            <label
                                className="font-normal mb-2"
                                style={{
                                    color: Colors.LabelColor,
                                }}
                            >
                                Repeat Days
                            </label>
                            <Checkbox.Group
                                options={daysOfWeek}
                                value={value}
                                onChange={onChange}
                                className="flex flex-wrap gap-2"
                            />
                            {fieldState?.error && (
                                <p style={{ color: Colors.Error }}>
                                    {fieldState?.error?.message}
                                </p>
                            )}
                        </div>
                    )}
                />
            </Col>
        );
    };

    return (
        <>
            <BasicDrawer
                openDrawer={drawerStates?.shiftTypeDrawer}
                width={500}
                title={
                    drawerStates?.typeOfShift == 'open'
                        ? 'Open Shift'
                        : 'Normal Shift'
                }
                onClose={() => shiftCloseHandler()}
                children={
                    <>
                        <form>
                            <Row gutter={[16]} className="mb-4">
                                {drawerStates?.typeOfShift == 'normal' ? (
                                    <Col lg={24} md={24} sm={24} xs={24}>
                                        <Controller
                                            name="assignTo"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <LabelWithSelect
                                                        options={
                                                            userOptions
                                                                ? userOptions
                                                                : []
                                                        }
                                                        showSearch={true}
                                                        allowClear={true}
                                                        labeltext={'Assign To'}
                                                        field={field}
                                                        labelClass={
                                                            'font-normal'
                                                        }
                                                        inputClass={
                                                            'outline-none py-1 rounded h-9'
                                                        }
                                                    />

                                                    {fieldState?.error && (
                                                        <p
                                                            style={{
                                                                color: Colors.Error,
                                                            }}
                                                        >
                                                            {
                                                                fieldState
                                                                    ?.error
                                                                    ?.message
                                                            }
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                ) : (
                                    <Col lg={24} md={24} sm={24} xs={24}>
                                        <Controller
                                            name="numberOfAssignees"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <>
                                                    <LabelWithField
                                                        labeltext={
                                                            'Number Of Shifts'
                                                        }
                                                        field={field}
                                                        labelClass={
                                                            'font-normal'
                                                        }
                                                        inputClass={
                                                            'outline-none py-1 rounded h-9'
                                                        }
                                                    />
                                                    {fieldState?.error && (
                                                        <p
                                                            style={{
                                                                color: Colors.Error,
                                                            }}
                                                        >
                                                            {
                                                                fieldState
                                                                    ?.error
                                                                    ?.message
                                                            }
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Col>
                                )}

                                {/* <Col lg={11} md={11} sm={24} xs={24}>
                                    <Controller
                                        name="break"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <div className="">
                                                <LabelWithSelect
                                                    options={shiftBreaks}
                                                    labeltext={'Un-paid Break'}
                                                    field={field}
                                                    labelClass={'font-normal'}
                                                    inputClass={
                                                        'outline-none py-1 rounded h-9'
                                                    }
                                                />
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col> */}
                            </Row>

                            <Row gutter={[16, 32]} className="">
                                 <Col lg={13} md={13} sm={24} xs={24}>
                                    <Controller
                                        name="startTime"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <>
                                                <div className="flex flex-col">
                                                    <label
                                                        style={{
                                                            color: Colors.LabelColor,
                                                        }}
                                                        className="font-normal"
                                                    >
                                                        Start Time
                                                    </label>
                                                    <SmartTimePicker
                                                        value={state?.startTime || null}
                                                        use12Hours={timeFormat?.includes('A')}
                                                        onChange={(time) => {
                                                            setState((prev) => ({ ...prev, startTime: time }));
                                                            setValue('startTime', time?.format('HH:mm'));
                                                        }}
                                                        className="outline-none rounded-md h-9 w-full"
                                                    />
                                                    {validation?.startTime && (
                                                        <p
                                                            style={{
                                                                color: Colors.Error,
                                                            }}
                                                        >
                                                            {validation?.startTime}
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col lg={11} md={11} sm={24} xs={24}>
                                    <Controller
                                        name="endTime"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <>
                                                <div className="flex flex-col">
                                                    <label
                                                        style={{
                                                            color: Colors.LabelColor,
                                                        }}
                                                        className="font-normal"
                                                    >
                                                        End Time
                                                    </label>
                                                    <SmartTimePicker
                                                        value={state?.endTime || null}
                                                        use12Hours={timeFormat?.includes('A')}
                                                        onChange={(time) => {
                                                            setState((prev) => ({ ...prev, endTime: time }));
                                                            setValue('endTime', time?.format('HH:mm'));
                                                        }}
                                                        className="outline-none rounded-md h-9 w-full"
                                                    />
                                                    {validation?.endTime && (
                                                        <p
                                                            style={{
                                                                color: Colors.Error,
                                                            }}
                                                        >
                                                            {
                                                                validation?.endTime
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row
                                gutter={[16]}
                                className="my-2 flex items-center"
                            >
                                <Col
                                    lg={13}
                                    md={13}
                                    sm={24}
                                    xs={24}
                                    className=""
                                >
                                    <Controller
                                        name="propertyId"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <>
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    Property Site
                                                </label>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Select: {
                                                                optionSelectedBg:
                                                                    colorPrimary,
                                                                optionActiveBg:
                                                                    Colors.LightGray,
                                                                optionSelectedColor:
                                                                    Colors.White,
                                                                selectorBg:
                                                                    Colors.LightGray,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <PaginatedSelect
                                                        allowClear={true}
                                                        fetchPage={
                                                            fetchPropertiesPage
                                                        }
                                                        showFullLabelToggle
                                                        enabled={
                                                            drawerStates.typeOfShift ==
                                                            'normal' ||
                                                            drawerStates.typeOfShift ==
                                                            'open'
                                                        }
                                                        extraOptions={
                                                            selectedPropertyOption
                                                        }
                                                        placeholder="Search property site"
                                                        className="w-full h-9 outline-none rounded"
                                                        value={
                                                            value || undefined
                                                        }
                                                        onChange={(val) => {
                                                            onChange(val);
                                                            propertyApiHandler(
                                                                val,
                                                            );
                                                        }}
                                                    />
                                                </ConfigProvider>
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                ?.message
                                                        }
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col lg={11} md={11} sm={24} xs={24}>
                                    <Controller
                                        name="addressId"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <>
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    Address
                                                </label>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Select: {
                                                                optionSelectedBg:
                                                                    colorPrimary,
                                                                optionActiveBg:
                                                                    Colors.LightGray,
                                                                optionSelectedColor:
                                                                    Colors.White,
                                                                selectorBg:
                                                                    Colors.LightGray,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <PaginatedSelect
                                                        allowClear
                                                        items={addressOptions}
                                                        className="w-full h-9 outline-none rounded"
                                                        disabled={
                                                            !selectedProperty
                                                        }
                                                        placeholder={
                                                            selectedProperty
                                                                ? 'Search address'
                                                                : 'Select a Property Site first'
                                                        }
                                                        value={
                                                            value || undefined
                                                        }
                                                        onChange={onChange}
                                                    />
                                                </ConfigProvider>
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                ?.message
                                                        }
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            {/* Address Field — populated from selectedProperty.addresses */}
                            <Row className="mb-4">
                                <Col lg={13} md={13} sm={24} xs={24}>
                                    <Controller
                                        name="eligibilityId"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <div className="">
                                                <LabelWithSelect
                                                    options={
                                                        elegibilityOptions
                                                            ? elegibilityOptions
                                                            : []
                                                    }
                                                    labeltext={'Position'}
                                                    field={field}
                                                    labelClass={'font-normal'}
                                                    inputClass={
                                                        'outline-none py-1 rounded h-9'
                                                    }
                                                />
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row
                                gutter={[8, 32]}
                                className="flex items-center justify-between w-full"
                            >
                                {drawerStates?.typeOfShift !== 'open' ? (
                                    <>
                                        <Col lg={13} md={13} sm={24} xs={24}>
                                            <label
                                                className="font-normal"
                                                style={{
                                                    color: Colors.LabelColor,
                                                }}
                                            >
                                                Shift Types
                                            </label>
                                            <div>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Select: {
                                                                optionSelectedBg:
                                                                    colorPrimary,
                                                                optionActiveBg:
                                                                    Colors.LightGray,
                                                                optionSelectedColor:
                                                                    Colors.White,
                                                                selectorBg:
                                                                    Colors.LightGray,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Select
                                                        defaultValue={shiftType}
                                                        onChange={(value) =>
                                                            setShiftType(value)
                                                        }
                                                        className="w-full"
                                                    >
                                                        <Option value="non-reoccurring">
                                                            Non Re-occurring
                                                        </Option>
                                                        <Option value="reoccurring">
                                                            Re-occurring
                                                        </Option>
                                                    </Select>
                                                </ConfigProvider>
                                            </div>
                                        </Col>

                                        {shiftType === 'non-reoccurring' ? (
                                            <Col
                                                lg={13}
                                                md={13}
                                                sm={24}
                                                xs={24}
                                            >
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    Date
                                                </label>
                                                <div>
                                                    <Controller
                                                        name="shiftDate"
                                                        control={control}
                                                        render={({
                                                            field: {
                                                                value,
                                                                onChange,
                                                            },
                                                            fieldState,
                                                        }) => (
                                                            <>
                                                                <DatePicker
                                                                    className="outline-none py-1 rounded-md h-9 w-full"
                                                                    allowClear={
                                                                        true
                                                                    }
                                                                    format={
                                                                        dateFormat
                                                                    }
                                                                    value={
                                                                        state.shiftDate
                                                                            ? dayjs(
                                                                                state.shiftDate,
                                                                                dateFormat,
                                                                            )
                                                                            : null
                                                                    }
                                                                    defaultValue={dayjs(
                                                                        dateFormat,
                                                                    )}
                                                                    onChange={(
                                                                        date,
                                                                        dateString,
                                                                    ) => {
                                                                        setState(
                                                                            (
                                                                                prev,
                                                                            ) => ({
                                                                                ...prev,
                                                                                shiftDate:
                                                                                    dateString,
                                                                            }),
                                                                        );
                                                                        setValue(
                                                                            'shiftDate',
                                                                            dateString,
                                                                        );
                                                                    }}
                                                                />
                                                                {validation?.shiftDate && (
                                                                    <p
                                                                        style={{
                                                                            color: Colors.Error,
                                                                        }}
                                                                    >
                                                                        {
                                                                            validation.shiftDate
                                                                        }
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                    />
                                                </div>
                                            </Col>
                                        ) : (
                                            <Col
                                                lg={13}
                                                md={13}
                                                sm={24}
                                                xs={24}
                                            >
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    Date Range
                                                </label>
                                                <div>
                                                    <Controller
                                                        name="startDate"
                                                        control={control}
                                                        render={({
                                                            field: {
                                                                value,
                                                                onChange,
                                                            },
                                                            fieldState,
                                                        }) => (
                                                            <>
                                                                <RangeDatePicker
                                                                    allowClear={
                                                                        true
                                                                    }
                                                                    format={
                                                                        dateFormat
                                                                    }
                                                                    className={
                                                                        'outline-none py-1 rounded-md h-9 w-full'
                                                                    }
                                                                    value={
                                                                        state?.startDate
                                                                            ? [
                                                                                dayjs(
                                                                                    state?.startDate,
                                                                                ),
                                                                                dayjs(
                                                                                    state?.endDate,
                                                                                ),
                                                                            ]
                                                                            : [
                                                                                dayjs(),
                                                                                dayjs(),
                                                                            ]
                                                                    }
                                                                    defaultValue={
                                                                        state?.startDate
                                                                            ? [
                                                                                dayjs(
                                                                                    state?.startDate,
                                                                                ),
                                                                                dayjs(
                                                                                    state?.endDate,
                                                                                ),
                                                                            ]
                                                                            : [
                                                                                dayjs(),
                                                                                dayjs(),
                                                                            ]
                                                                    }
                                                                    onChange={(
                                                                        date,
                                                                        dateStrings,
                                                                    ) => {
                                                                        setState(
                                                                            (
                                                                                prev,
                                                                            ) => ({
                                                                                ...prev,
                                                                                startDate:
                                                                                    date &&
                                                                                    date[0],
                                                                                endDate:
                                                                                    date &&
                                                                                    date[1],
                                                                            }),
                                                                        );
                                                                        setValue(
                                                                            'startDate',
                                                                            dateStrings &&
                                                                            dateStrings[0],
                                                                        );
                                                                        setValue(
                                                                            'endDate',
                                                                            dateStrings &&
                                                                            dateStrings[1],
                                                                        );
                                                                    }}
                                                                />
                                                                {validation?.startDate && (
                                                                    <p
                                                                        style={{
                                                                            color: Colors.Error,
                                                                        }}
                                                                    >
                                                                        {
                                                                            validation?.startDate
                                                                        }
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                    />
                                                </div>
                                            </Col>
                                        )}

                                        {shiftType === 'reoccurring' && (
                                            <Col
                                                lg={11}
                                                md={11}
                                                sm={24}
                                                xs={24}
                                                className="w-full mb-2"
                                                style={{
                                                    paddingRight: 0,
                                                    paddingLeft: '4px',
                                                }}
                                            >
                                                <Controller
                                                    name="recurringFrequency"
                                                    control={control}
                                                    render={({
                                                        field,
                                                        fieldState,
                                                    }) => (
                                                        <div
                                                            className="w-full"
                                                            style={{
                                                                paddingRight: 0,
                                                                paddingLeft:
                                                                    '4px',
                                                            }}
                                                        >
                                                            <LabelWithSelect
                                                                options={
                                                                    recurringFrequency
                                                                }
                                                                labeltext="Repeat Shift"
                                                                field={field}
                                                                labelClass="font-normal"
                                                                inputClass="outline-none py-1 rounded h-9"
                                                            />
                                                            {fieldState?.error && (
                                                                <p
                                                                    style={{
                                                                        color: Colors.Error,
                                                                    }}
                                                                >
                                                                    {
                                                                        fieldState
                                                                            ?.error
                                                                            ?.message
                                                                    }
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                />
                                            </Col>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Col lg={13} md={13} sm={24} xs={24}>
                                            <label
                                                className="font-normal"
                                                style={{
                                                    color: Colors.LabelColor,
                                                }}
                                            >
                                                Shift Types
                                            </label>
                                            <div>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Select: {
                                                                optionSelectedBg:
                                                                    colorPrimary,
                                                                optionActiveBg:
                                                                    Colors.LightGray,
                                                                optionSelectedColor:
                                                                    Colors.White,
                                                                selectorBg:
                                                                    Colors.LightGray,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Select
                                                        defaultValue={shiftType} // Set default shift type
                                                        onChange={(value) =>
                                                            setShiftType(value)
                                                        } // Update shift type state on change
                                                        className="w-full"
                                                    >
                                                        <Option value="non-reoccurring">
                                                            Non Re-occurring
                                                        </Option>
                                                        <Option value="reoccurring">
                                                            Re-occurring
                                                        </Option>
                                                    </Select>
                                                </ConfigProvider>
                                            </div>
                                        </Col>

                                        {shiftType === 'non-reoccurring' ? (
                                            <Col
                                                lg={13}
                                                md={13}
                                                sm={24}
                                                xs={24}
                                            >
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    Date
                                                </label>
                                                <div>
                                                    <Controller
                                                        name="shiftDate"
                                                        control={control}
                                                        render={({
                                                            field: {
                                                                value,
                                                                onChange,
                                                            },
                                                            fieldState,
                                                        }) => (
                                                            <>
                                                                <DatePicker
                                                                    className="outline-none py-1 rounded-md h-9 w-full"
                                                                    allowClear={
                                                                        true
                                                                    }
                                                                    format={
                                                                        dateFormat
                                                                    }
                                                                    value={
                                                                        state.shiftDate
                                                                            ? dayjs(
                                                                                state.shiftDate,
                                                                                dateFormat,
                                                                            )
                                                                            : null
                                                                    }
                                                                    defaultValue={
                                                                        state?.shiftDate
                                                                            ? dayjs(
                                                                                state?.shiftDate,
                                                                            )
                                                                            : dayjs()
                                                                    }
                                                                    onChange={(
                                                                        date,
                                                                        dateString,
                                                                    ) => {
                                                                        setState(
                                                                            (
                                                                                prev,
                                                                            ) => ({
                                                                                ...prev,
                                                                                shiftDate:
                                                                                    dateString,
                                                                            }),
                                                                        );
                                                                        setValue(
                                                                            'shiftDate',
                                                                            dateString,
                                                                        );
                                                                    }}
                                                                />
                                                                {validation?.shiftDate && (
                                                                    <p
                                                                        style={{
                                                                            color: Colors.Error,
                                                                        }}
                                                                    >
                                                                        {
                                                                            validation.shiftDate
                                                                        }
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                    />
                                                </div>
                                            </Col>
                                        ) : (
                                            <Col
                                                lg={13}
                                                md={13}
                                                sm={24}
                                                xs={24}
                                            >
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    Date Range
                                                </label>
                                                <div>
                                                    <Controller
                                                        name="startDate"
                                                        control={control}
                                                        render={({
                                                            field: {
                                                                value,
                                                                onChange,
                                                            },
                                                            fieldState,
                                                        }) => (
                                                            <>
                                                                <RangeDatePicker
                                                                    allowClear={
                                                                        true
                                                                    }
                                                                    format={
                                                                        dateFormat
                                                                    }
                                                                    className={
                                                                        'outline-none py-1 rounded-md h-9 w-full'
                                                                    }
                                                                    value={
                                                                        state?.startDate
                                                                            ? [
                                                                                dayjs(
                                                                                    state?.startDate,
                                                                                ),
                                                                                dayjs(
                                                                                    state?.endDate,
                                                                                ),
                                                                            ]
                                                                            : [
                                                                                dayjs(),
                                                                                dayjs(),
                                                                            ]
                                                                    }
                                                                    defaultValue={
                                                                        state?.startDate
                                                                            ? [
                                                                                dayjs(
                                                                                    state?.startDate,
                                                                                ),
                                                                                dayjs(
                                                                                    state?.endDate,
                                                                                ),
                                                                            ]
                                                                            : [
                                                                                dayjs(),
                                                                                dayjs(),
                                                                            ]
                                                                    }
                                                                    onChange={(
                                                                        date,
                                                                        dateStrings,
                                                                    ) => {
                                                                        setState(
                                                                            (
                                                                                prev,
                                                                            ) => ({
                                                                                ...prev,
                                                                                startDate:
                                                                                    date &&
                                                                                    date[0],
                                                                                endDate:
                                                                                    date &&
                                                                                    date[1],
                                                                            }),
                                                                        );
                                                                        setValue(
                                                                            'startDate',
                                                                            dateStrings &&
                                                                            dateStrings[0],
                                                                        );
                                                                        setValue(
                                                                            'endDate',
                                                                            dateStrings &&
                                                                            dateStrings[1],
                                                                        );
                                                                    }}
                                                                />
                                                                {validation?.startDate && (
                                                                    <p
                                                                        style={{
                                                                            color: Colors.Error,
                                                                        }}
                                                                    >
                                                                        {
                                                                            validation?.startDate
                                                                        }
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                    />
                                                </div>
                                            </Col>
                                        )}
                                        {/* {shiftType === 'reoccurring' && <DaysOfWeek />} */}

                                        {/* Add any additional fields like recurring frequency, if needed */}
                                        {shiftType === 'reoccurring' && (
                                            <>
                                                <Col
                                                    lg={11}
                                                    md={11}
                                                    sm={24}
                                                    xs={24}
                                                    className="w-full mb-2"
                                                    style={{
                                                        paddingRight: 0,
                                                        paddingLeft: '4px',
                                                    }}
                                                >
                                                    <Controller
                                                        name="recurringFrequency"
                                                        control={control}
                                                        render={({
                                                            field,
                                                            fieldState,
                                                        }) => (
                                                            <div
                                                                className="w-full"
                                                                style={{
                                                                    paddingRight: 0,
                                                                    paddingLeft:
                                                                        '4px',
                                                                }}
                                                            >
                                                                <LabelWithSelect
                                                                    options={
                                                                        recurringFrequency
                                                                    }
                                                                    labeltext="Repeat Shift"
                                                                    field={
                                                                        field
                                                                    }
                                                                    labelClass="font-normal"
                                                                    inputClass="outline-none py-1 rounded h-9"
                                                                />
                                                                {fieldState?.error && (
                                                                    <p
                                                                        style={{
                                                                            color: Colors.Error,
                                                                        }}
                                                                    >
                                                                        {
                                                                            fieldState
                                                                                ?.error
                                                                                ?.message
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                    />
                                                </Col>
                                            </>
                                        )}
                                    </>
                                )}
                            </Row>
                            {shiftType === 'reoccurring' && <DaysOfWeek />}

                            <Row className="mb-4">
                                <Col lg={13} md={11} sm={24} xs={24}>
                                    <Controller
                                        name="locationId"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <>
                                                <label
                                                    className="font-normal"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    Location
                                                </label>
                                                <ConfigProvider
                                                    theme={{
                                                        components: {
                                                            Select: {
                                                                optionSelectedBg:
                                                                    colorPrimary,
                                                                optionActiveBg:
                                                                    Colors.LightGray,
                                                                optionSelectedColor:
                                                                    Colors.White,
                                                                selectorBg:
                                                                    Colors.LightGray,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <PaginatedSelect
                                                        allowClear
                                                        items={locationOptions}
                                                        className="w-full h-9 outline-none rounded"
                                                        disabled={
                                                            !selectedProperty
                                                        }
                                                        placeholder={
                                                            selectedProperty
                                                                ? 'Search location'
                                                                : 'Select a Property Site first'
                                                        }
                                                        value={
                                                            value || undefined
                                                        }
                                                        onChange={onChange}
                                                    />
                                                </ConfigProvider>
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                ?.message
                                                        }
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row gutter={[16, 32]}>
                                <Col span={24}>
                                    <Controller
                                        name="notes"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                            fieldState,
                                        }) => (
                                            <div className="">
                                                <TextField.Textarea
                                                    style={{
                                                        color: Colors.LightGray,
                                                    }}
                                                    placeholder={''}
                                                    value={value}
                                                    onChange={onChange}
                                                    labeltext={'Note'}
                                                    className={
                                                        'outline-none h-10 rounded'
                                                    }
                                                />
                                                {fieldState?.error && (
                                                    <p
                                                        style={{
                                                            color: Colors.Error,
                                                        }}
                                                    >
                                                        {
                                                            fieldState?.error
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Row gutter={[16, 32]} className="my-6">
                                <Col span={12} className="flex items-center">
                                    <Controller
                                        name="isPublished"
                                        control={control}
                                        render={({
                                            field: { value, onChange },
                                        }) => (
                                            <div className="flex items-center gap-3">
                                                <Switch
                                                    disabled={
                                                        userRole == 'manager'
                                                    }
                                                    checked={value}
                                                    onChange={onChange}
                                                    className="custom-switch"
                                                    style={{
                                                        '--myColor':
                                                            colorPrimary,
                                                    }}
                                                />
                                                <span
                                                    className="font-medium"
                                                    style={{
                                                        color: Colors.LabelColor,
                                                    }}
                                                >
                                                    {value
                                                        ? 'Published'
                                                        : 'Draft'}
                                                </span>
                                            </div>
                                        )}
                                    />
                                </Col>
                                <Col
                                    span={12}
                                    className="flex items-center justify-end"
                                >
                                    <Button.Basic
                                        onClick={handleSubmit(shiftSaveHandler)}
                                        htmlType="submit"
                                        loader={
                                            normalShiftMutation?.isLoading ||
                                            openShiftMutation.isLoading
                                        }
                                        size={'large'}
                                        text={'Save'}
                                        buttonStyle={{
                                            backgroundColor: colorPrimary,
                                            color: 'white',
                                        }}
                                        className={'px-12 rounded-md h-12'}
                                    />
                                </Col>
                            </Row>
                        </form>
                    </>
                }
            />
        </>
    );
};

export default AssignShiftDrawer;
